/**
 * Helper services to support the checkout process.
 * Methods not directly related to the get and set of behaviour subjects or fetching of api data.
 */
import { Injectable } from '@angular/core';
import { ValidatorFn, Validators } from '@angular/forms';
import { ProductMembershipsService } from '@app/products/product-memberships/product-memberships.service';
import { CMembership } from '@app/tickets/tickets.model';
import { TranslateService } from '@ngx-translate/core';
import { BookingCalculationService, roundToTwo } from '@roller/booking-calculation-service';
import { MinBookingLimitValidator } from '../form/validators/min-booking-limit/min-booking-limit.validator';
import { TPaymentFrequency } from '../payment/enums/payment-frequency.enum';
import { IProductInfo } from '../product/models/product-info.model';
import { IVenueCostSetting } from '../venue-config/models/venue-cost-setting.model';
import { VenueCurrencyPipe } from '../venue-currency/venue-currency.pipe';
import { IOrderItem } from '@core/order/models/order-item.model';
import { VenueConfigService } from '@core/venue-config/venue-config.service';
import { CMaxOrderItems } from '@core/availability-calculation/availability-calculation.const';
import { rdlIsNumber } from '@roller/rdl/utilities/number';
import { ICurrencySettings } from './currency-settings.model';
import { ICheckoutRecurringPayment } from '../product/models/checkout-recurring-payment.model';

@Injectable({
  providedIn: 'root'
})
export class CheckoutHelperService {
  venueCurrencyPipe = new VenueCurrencyPipe();
  constructor(
    private minBookingLimit: MinBookingLimitValidator,
    private translateService: TranslateService,
    private calculationService: BookingCalculationService,
    private productMembershipService: ProductMembershipsService,
    private venueConfigService: VenueConfigService
  ) {}

  /**
   * Adds, min, minBookingLimit and max Validators to number-increment form controls.
   * The max should be the total the increment value can be, and always be pre-calculated based on purchase limits and capacity remaining
   */
  addMinMaxValidators(info: IProductInfo | undefined, totalMax?: number): ValidatorFn[] {
    if (!info) {
      return [];
    }

    const validateItem: ValidatorFn[] = [Validators.min(0)];
    if (info.minPurchase) {
      validateItem.push(this.minBookingLimit.minBookingLimit(0, info.minPurchase));
    }

    let maxValidator = CMaxOrderItems;
    if (rdlIsNumber(totalMax)) {
      maxValidator = totalMax;
    }

    validateItem.push(Validators.max(maxValidator));

    return validateItem;
  }

  /**
   * Builds a recurring payment message.
   * * no number of payments implies ongoing recurring payments
   * * paymentFrequencyId has a plural based on daily, monthly, weekly or year
   * Your card will be charged sum/month ongoing / for numberOfPayments month(s) - some parts are <strong>
   * @param basket current list of orderItems.
   * @param total bookingTotal
   * @returns html based string.
   */
  buildRecurringPaymentMessage(basket: IOrderItem[], total?: number, isSubscriptionTerms = false): string | undefined {
    const recurringItems = this.getRecurringItems(basket);
    const firstRecurringItem = recurringItems[0];
    const recur = firstRecurringItem?.recurringPayment;

    if (!recurringItems.length || !recur) {
      return undefined;
    }

    const currency = this.venueConfigService.getCurrencySettings();
    const config = this.venueConfigService.getVenueConfig();
    const amountDollar = this.calcRecurringCost(recurringItems, config.costSettings);
    const amountFormatted = this.venueCurrencyPipe.transform(amountDollar, currency) || '';
    const frequencyText = this.calcFrequency(recur.paymentFrequencyId);

    let message = this.buildBaseRecurringMessage();

    if (this.hasUpgradedMembership(recurringItems)) {
      return this.buildUpgradedMembershipMessage(
        message,
        recurringItems,
        frequencyText,
        currency,
        config.costSettings,
        total
      );
    }

    message += this.buildAmountFrequencyMessage(amountFormatted, frequencyText);
    message += this.buildAmountFrequencyPerMembershipMessage(
      isSubscriptionTerms,
      firstRecurringItem,
      amountDollar,
      frequencyText,
      currency
    );
    message += this.buildPaymentPeriodMessage(recur);
    message += this.buildMinimumObligationMessage(recurringItems);

    return message;
  }

  private buildBaseRecurringMessage() {
    const paymentMethod = this.translateService.instant('PAYMENT_METHODS.CARD').toLocaleLowerCase();
    return this.translateService.instant('RECURRING_BILLING.RECURRING_BILLING_SUMMARY', {
      paymentMethod
    });
  }

  private buildUpgradedMembershipMessage(
    baseMessage: string,
    recurringItems: IOrderItem[],
    frequencyText: string,
    currency: ICurrencySettings,
    costSettings: IVenueCostSetting,
    total = 0
  ) {
    let message = baseMessage;

    const amountFormatted = this.getUpgradedMembershipCost(recurringItems, currency, costSettings);
    const upgradeTotal = this.venueCurrencyPipe.transform(total, currency);
    message += this.translateService.instant('RECURRING_BILLING.UPGRADED_MEMBERSHIP', {
      TOTAL: upgradeTotal
    });

    message += `<strong>${this.translateService.instant('RECURRING_BILLING.AMOUNT_FREQUENCY', {
      amount: amountFormatted,
      frequency: frequencyText
    })}${this.translateService.instant('RECURRING_BILLING.ONGOING')}${this.translateService.instant(
      'RECURRING_BILLING.BEGINNING_TODAY'
    )}</strong>`;

    return message;
  }

  private buildAmountFrequencyMessage(amountFormatted: string, frequencyText: string) {
    return `<strong>${this.translateService.instant('RECURRING_BILLING.AMOUNT_FREQUENCY', {
      amount: amountFormatted,
      frequency: frequencyText
    })}</strong>`;
  }

  private buildPaymentPeriodMessage(recur: ICheckoutRecurringPayment) {
    let message = '';

    if (recur.numberOfPayments) {
      message = `<strong>${this.translateService.instant('RECURRING_BILLING.FOR_PERIOD', {
        numberOfPayments: recur.numberOfPayments,
        period: this.calcFrequency(recur.paymentFrequencyId, recur.numberOfPayments)
      })}</strong>`;
    } else {
      message = `<strong>${this.translateService.instant('RECURRING_BILLING.ONGOING')}</strong>`;
    }

    message += `<strong>${this.translateService.instant('RECURRING_BILLING.BEGINNING_TODAY')}</strong>`;

    return message;
  }

  private buildMinimumObligationMessage(recurringItems: IOrderItem[]) {
    const filteredRecurringMin = recurringItems.filter(
      (oItem: IOrderItem) => !!oItem.recurringPayment?.minNumberOfPayments
    );

    if (!filteredRecurringMin.length) {
      return '';
    }

    let message = this.translateService.instant('RECURRING_BILLING.MIN_OBLIGATION_PLURAL_A');
    filteredRecurringMin.forEach((oItem: IOrderItem, index: number) => {
      const minPayments = oItem.recurringPayment?.minNumberOfPayments;
      const frequencyText = this.calcFrequency(oItem.recurringPayment?.paymentFrequencyId, minPayments || 1);
      message += `<strong>${this.translateService.instant('RECURRING_BILLING.MIN_OBLIGATION_PLURAL_B', {
        value: minPayments,
        frequency: frequencyText,
        productName: oItem.parentName,
        ticketName: oItem.product.name
      })}`;
      if (index < filteredRecurringMin.length - 1) {
        message += this.translateService.instant('GENERIC.AND');
      } else {
        message += '</strong>)';
      }
    });

    return message;
  }

  private buildAmountFrequencyPerMembershipMessage(
    isSubscriptionTerms: boolean,
    recurringItem: IOrderItem,
    amountDollarTotal: number,
    frequencyText: string,
    currency: ICurrencySettings
  ) {
    if (!isSubscriptionTerms || recurringItem.quantity <= 1) {
      return '';
    }

    const amountPerMembershipDollar = roundToTwo(amountDollarTotal / recurringItem.quantity);
    const amountPerMembershipFormatted = this.venueCurrencyPipe.transform(amountPerMembershipDollar, currency);

    return `<strong>${this.translateService.instant('RECURRING_BILLING.OR_AMOUNT_FREQUENCY_PER_MEMBERSHIP', {
      amount: amountPerMembershipFormatted,
      frequency: frequencyText
    })}</strong>`;
  }

  private hasUpgradedMembership(items: IOrderItem[]) {
    return items.some((item) => item.isManageItemModified && item.manageBookingType === CMembership);
  }

  private getRecurringItems(basket: IOrderItem[]): IOrderItem[] {
    return basket.filter(
      (item) => !!item.recurringPayment && !this.productMembershipService.isUnmodifiedMembership(item)
    );
  }

  private getUpgradedMembershipCost(
    recurringItems: IOrderItem[],
    currency: ICurrencySettings,
    costSettings: IVenueCostSetting
  ) {
    const basket = structuredClone(recurringItems).map((item: IOrderItem) => ({
      ...item,
      cost: item.manageMembership?.upgradeCost || 0
    }));

    const amountDollar = this.calcRecurringCost(basket, costSettings);
    return this.venueCurrencyPipe.transform(amountDollar, currency) || '';
  }

  /** Calculates the cost including tax on recurring payments. Note that transaction fees are not included on recurring payments. */
  private calcRecurringCost(recurringItems: IOrderItem[], costSettings: IVenueCostSetting): number {
    return recurringItems.reduce((acc: number, oItem: IOrderItem) => {
      let itemCost = oItem.cost * oItem.quantity;

      if (!this.calculationService.isItemTaxInclusive(oItem, costSettings)) {
        itemCost += this.calculationService.getTicketTax(oItem, costSettings) * oItem.quantity;
      }

      return itemCost + acc;
    }, 0);
  }

  /** Quick private method to calc translated frequency using full translation key */
  private calcFrequency(paymentFrequencyId?: TPaymentFrequency, payments = 1): string {
    if (!paymentFrequencyId) {
      return '';
    }

    const keyObject = {
      daily: 'RECURRING_BILLING.FREQUENCY.DAILY',
      weekly: 'RECURRING_BILLING.FREQUENCY.WEEKLY',
      monthly: 'RECURRING_BILLING.FREQUENCY.MONTHLY',
      yearly: 'RECURRING_BILLING.FREQUENCY.YEARLY'
    };

    return this.translateService.instant(keyObject[paymentFrequencyId], { PAYMENTS: payments });
  }
}
